import {Component, inject, OnInit, signal} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {firstValueFrom} from 'rxjs';
import {LogRetentionSettingDto, LogRetentionSettingService} from './log-retention-setting-service';
import {ConfirmDialogService} from '../../../shared/ui/confirm-dialog/confirm-dialog.service';
import {PageTitleComponent} from '../../../shared/commons/page-title/page-title.component';
import {ToastService} from '../../../shared/commons/toast/toast-service';
import {ThaiDateTimePipe} from '../../../shared/commons/thai-datetime.pipe';

export const LOG_RETENTION_MIN_DAYS = 1;
export const LOG_RETENTION_MAX_DAYS = 366;

/**
 * ตั้งค่าจำนวนวันเก็บ Log (เทียบเท่า auto_delete_log ในระบบเดิม)
 * ค่านี้ถูกใช้โดย job ลบ Log อัตโนมัติ และแสดงในกล่องหมายเหตุของหน้ารายงาน Log
 */
@Component({
  selector: 'app-log-retention-setting',
  standalone: true,
  imports: [ReactiveFormsModule, PageTitleComponent, ThaiDateTimePipe],
  templateUrl: './log-retention-setting.html',
  styleUrl: './log-retention-setting.scss',
})
export class LogRetentionSettingComponent implements OnInit {
  private readonly service = inject(LogRetentionSettingService);
  private readonly fb = inject(FormBuilder);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly toastService = inject(ToastService);

  protected readonly minDays = LOG_RETENTION_MIN_DAYS;
  protected readonly maxDays = LOG_RETENTION_MAX_DAYS;
  readonly isLoading = signal(true);
  readonly isSubmitting = signal(false);
  readonly updatedAt = signal<string | null>(null);
  submitted = false;

  readonly form: FormGroup = this.fb.group({
    id: [1],
    retentionDays: [90, [Validators.required, Validators.min(LOG_RETENTION_MIN_DAYS), Validators.max(LOG_RETENTION_MAX_DAYS)]],
  });

  ngOnInit(): void {
    this.service.getSetting().subscribe({
      next: (data: LogRetentionSettingDto) => {
        this.form.patchValue({id: data.id ?? 1, retentionDays: data.retentionDays});
        this.updatedAt.set(data.updatedAt ?? null);
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false),
    });
  }

  async save(): Promise<void> {
    this.submitted = true;
    if (this.form.invalid || this.isSubmitting()) {
      return;
    }
    const confirmed = await this.confirmDialogService.confirmSave();
    if (!confirmed) {
      return;
    }
    this.isSubmitting.set(true);
    try {
      const saved = await firstValueFrom(this.service.saveSetting(this.form.value));
      this.updatedAt.set(saved.updatedAt ?? null);
      this.toastService.success('บันทึกข้อมูลสำเร็จ');
    } catch (err: any) {
      this.toastService.error(err?.error?.message ?? 'เกิดข้อผิดพลาด ไม่สามารถบันทึกข้อมูลได้');
    } finally {
      this.isSubmitting.set(false);
    }
  }
}
